/**
 * collection of snowplow contexts.
 * json schemas: https://gitlab.justwatch.com/jw/jw-redshift/tree/master/iglu-schemas/schemas/com.justwatch
 */

import {
	MultiStepSportEvent,
	ObjectType,
	SingleStepSportEvent,
	SportObjectType,
	SportType,
} from '@/@types/graphql-types';
import { EntityHelper } from '@/helpers/entity-helper';
import { toRESTMonetizationType, toRESTPresentationType } from '@/helpers/graphql-model-helper';
import { SCORINGS } from '@/helpers/scoring-helper';
import type { Metric } from 'web-vitals';
import { PerformanceMetrics } from '@/helpers/tracking/performance/PerformanceMonitor';
import Provider from '@/interfaces/provider';
import { ClickoutContentType, ClickoutOfferDetails } from '@/interfaces/snowplow/clickout-context';
import { TitleContentType } from '@/interfaces/snowplow/title-context';
import { SportPageType } from '@/interfaces/sport-graphql';
import { Offer, TitleObjectType } from '@/interfaces/titles';
import { FilterCollection } from '@/stores/filter.store';
import { Route } from 'vue-router';
import { UtilsHelper } from '@/helpers/utils-helper';

// looks something like this: 'iglu:com.justwatch/title_context/jsonschema/1-0-0'
export type SnowplowSchema = string;

/**
 * Collection of snowplow context classes that can be created
 *
 * Note: every new Context needs to be added to the CONTEXT_CLASSES object at the end of the file
 */
export abstract class SnowplowContext {
	/**
	 * all SnowplowContexts need to have a __name property for unserialization reasons (server > client)
	 */
	abstract __name: string;

	toObject() {
		// filter out undefined or null values
		const data = Object.keys(this)
			.filter(key => !key.startsWith('__')) // exclude __name property in output
			.reduce((root: any, key: string) => {
				const value = (this as any)[key];
				if ([undefined, null].indexOf(value) === -1) {
					root[key] = value;
				}
				return root;
			}, {});

		return {
			schema: this.getSchema(),
			data,
		};
	}

	/**
	 * Schema is unique for each context.
	 */
	abstract getSchema(): SnowplowSchema;
}

export class SnowplowTitleContext extends SnowplowContext {
	__name = 'title';
	jwEntityId: string;

	constructor(
		public titleId: number,
		public objectType: string,
		public seasonNumber: number | null = null,
		public episodeNumber: number | null = null
	) {
		super();
		this.jwEntityId = EntityHelper.getTitleContextEntityId(objectType, titleId);
	}

	getSchema() {
		return 'iglu:com.justwatch/title_context/jsonschema/1-0-0';
	}
}

export class SnowplowTitleContextGraphql extends SnowplowContext {
	__name = 'title';
	jwEntityId: string;

	constructor(
		public titleId: number,
		public objectType: ObjectType,
		public seasonNumber: number | null = null,
		public episodeNumber: number | null = null,
		public contentType: TitleContentType | undefined = undefined
	) {
		super();
		this.objectType = objectType.toLowerCase() as ObjectType;
		this.jwEntityId = EntityHelper.getTitleContextEntityIdGraphql(objectType, titleId);
	}

	getSchema() {
		return 'iglu:com.justwatch/title_context/jsonschema/1-1-0';
	}
}

export type SnowplowTitleContextGraphqlPayload = ConstructorParameters<typeof SnowplowTitleContextGraphql>;

export class SnowplowPersonContext extends SnowplowContext {
	__name = 'person';
	constructor(public personId: number, public fullName: string) {
		super();
	}

	getSchema() {
		return 'iglu:com.justwatch/person_context/jsonschema/1-0-0';
	}
}

export class SnowplowCampaignContextV1 extends SnowplowContext {
	__name = 'campaign_v1';
	constructor(
		public network: string,
		public targetUrl: string,
		public targetId: number | null = null,
		public placement: string | null = null,
		public campaignId: number | null = null,
		public adgroupId: number | null = null,
		public creativeId: number | null = null,
		public adId: number | null = null,
		public videoId: number | null = null,
		public keyword: string | null = null,
		public audienceType: string | null = null
	) {
		super();
	}

	getSchema() {
		return 'iglu:com.justwatch/campaign_context/jsonschema/1-0-0';
	}
}

export class SnowplowCampaignContextV2 extends SnowplowContext {
	__name = 'campaign_v2';
	constructor(
		public buyer: string, // uct_b
		public supplier: string, // uct_s
		public inventory: string, // uct_i
		public adType: string, // uct_at
		public targetingName: string, // uct_tn
		public targetUrl: string,
		public placement: string | null = null, // uct_p
		public slotId: number | null = null, // uct_sid
		public campaignId: number | null = null, // uct_cid
		public adgroupId: number | null = null, // uct_adgid
		public creativeId: number | null = null, // uct_crid
		public adId: number | null = null, // uct_adid
		public slotSupplierId: number | null = null, // uct_ssid
		public subcampaignId: number | null = null,
		public eccId: number | null = null
	) {
		super();
	}

	getSchema() {
		return 'iglu:com.justwatch/campaign_context/jsonschema/2-0-0';
	}
}

export class SnowplowGeoContext extends SnowplowContext {
	__name = 'geo';
	constructor(
		public latitude: number,
		public longitude: number,
		public locator_type: 'geocoding' | 'geolocation',
		public accuracy: number | null = null
	) {
		super();
	}

	getSchema() {
		return 'iglu:com.justwatch/geo_context/jsonschema/1-0-0';
	}
}

export class SnowplowLoginContext extends SnowplowContext {
	__name = 'login';
	constructor(public jwLid: string) {
		super();
	}

	getSchema() {
		return 'iglu:com.justwatch/login_context/jsonschema/1-0-0';
	}
}

export class SnowplowCMPContext extends SnowplowContext {
	__name = 'cmp';
	constructor(public value: string) {
		super();
	}

	getSchema() {
		return 'iglu:com.justwatch/cmp_context/jsonschema/1-0-0';
	}
}

export class SnowplowFreemiumContext extends SnowplowContext {
	__name = 'freemium';
	constructor(public isHideTitlesFiltered: boolean, public tier: number = 1) {
		super();
	}

	getSchema() {
		return 'iglu:com.justwatch/freemium_context/jsonschema/1-0-0';
	}
}

export class SnowplowPackageContext extends SnowplowContext {
	__name: 'package';
	constructor(
		public packageId: number,
		public packageName: string,
		public parentType?: null | 'package' | 'bundle',
		public parentId?: number,
		public parentName?: string
	) {
		super();
	}

	getSchema() {
		return 'iglu:com.justwatch/package_context/jsonschema/1-0-0';
	}
}

// @TODO add logic which defines how the filterSettings are put into context
export class SnowplowFilterContext extends SnowplowContext {
	__name = 'filter';
	isProviderFiltered: boolean;
	providerId: string;
	isGenreFiltered: boolean;
	genreId: string;
	genreIdExcluded: string;
	isAgeRatingFiltered: boolean;
	ageRating: string | null;
	sortedBy: string | null;
	objectTypeFilter: number;
	isMonetizationTypeFiltered: boolean;
	monetizationType: string;
	isScoringFiltered: boolean;
	scoring: string;
	countryCode: string;
	languageCode: string;
	isMyStreamingServicesFiltered: boolean;
	searchString: string | null;
	releaseYearFrom: number | null;
	releaseYearUntil: number | null;
	minPrice: number | null;
	maxPrice: number | null;
	pageId: number | null;

	// Premium Filters
	isRuntimeFiltered: boolean;
	runtimeFrom: number | null;
	runtimeUntil: number | null;

	isImdbPopularityFiltered: boolean;
	imdbPopularityFrom?: number | null;
	imdbPopularityUntil?: number | null;

	isProductionCountryFiltered: boolean;
	productionCountries: string | null;
	productionCountriesExclude: string | null;

	constructor(
		filterState: FilterCollection,
		routeQuery: Route['query'],
		pageId: number,
		locale: string,
		providerShortNames: string[]
	) {
		super();

		const scoringValues = SCORINGS.filter(item => item.filterDisplay).reduce((root, item) => {
			root[item.provider] = { min_scoring_value: item.min, max_scoring_value: item.max };
			return root;
		}, {} as any);

		if (filterState) {
			// NON-NULLABLE
			this.isProviderFiltered = filterState.providers.length > 0;
			const selectedProviders = this.isProviderFiltered ? filterState['providers'] : providerShortNames;
			this.providerId = this.getFormattedArrayString(selectedProviders);

			this.isGenreFiltered = filterState.genres?.length > 0 || filterState.exclude_genres?.length > 0;
			this.genreId = this.getFormattedArrayString(filterState['genres']);
			this.genreIdExcluded = this.getFormattedArrayString(filterState['exclude_genres']);

			this.isAgeRatingFiltered = filterState.age_certifications?.length > 0;
			this.ageRating = this.getFormattedArrayString(filterState['age_certifications']);
			this.sortedBy = filterState.sort_by;

			if (filterState.content_types.indexOf(TitleObjectType.MOVIE) !== -1) {
				this.objectTypeFilter = 1;
			} else if (
				filterState.content_types.indexOf(TitleObjectType.SHOW) !== -1 ||
				filterState.content_types.indexOf(TitleObjectType.SEASON) !== -1
			) {
				this.objectTypeFilter = 2;
			} else {
				this.objectTypeFilter = 0;
			}
			this.isMonetizationTypeFiltered = filterState.monetization_types?.length > 0;
			this.monetizationType = this.getFormattedArrayString(filterState['monetization_types']);
			this.isScoringFiltered = Object.keys(filterState.scoring_filter_types || {}).length > 0;
			this.scoring = this.getStringifiedArrayValue(filterState, 'scoring_filter_types', scoringValues);
			const localeSplit = locale.split('_');
			this.languageCode = localeSplit[0].toLowerCase();
			this.countryCode = localeSplit[1].toLowerCase();
			// dummy value, as of 2020-12-15 this was only used in jw-tv and
			// did not make sense for jw-app as there's no such filter
			this.isMyStreamingServicesFiltered = false;
			// NULLABLE
			this.searchString = (routeQuery.q || '') as string;
			this.releaseYearFrom = filterState.release_year_from;
			this.releaseYearUntil = filterState.release_year_until;
			this.minPrice = filterState.min_price;
			this.maxPrice = filterState.max_price;
			this.pageId = pageId;

			//Premium Filters
			this.isRuntimeFiltered = !!(filterState.min_runtime || filterState.max_runtime);
			this.runtimeFrom = filterState.min_runtime;
			this.runtimeUntil = filterState.max_runtime;

			this.isImdbPopularityFiltered = !!filterState.scoring_filter_types?.['imdb:votes'];
			this.imdbPopularityFrom = filterState.scoring_filter_types?.['imdb:votes']?.min_scoring_value;
			this.imdbPopularityUntil = filterState.scoring_filter_types?.['imdb:votes']?.max_scoring_value;

			this.isProductionCountryFiltered =
				filterState.production_countries?.length > 0 || filterState.exclude_production_countries?.length > 0;
			this.productionCountries = this.getFormattedArrayString(filterState['production_countries']);
			this.productionCountriesExclude = this.getFormattedArrayString(filterState['exclude_production_countries']);
		}
	}

	private getStringifiedArrayValue(filterState: FilterCollection, key: keyof FilterCollection, fallback: string[]) {
		const value = ((filterState[key] as string[]) || []).length === 0 ? fallback : filterState[key];
		return JSON.stringify(value);
	}

	private getFormattedArrayString(filterValue: string[]) {
		return UtilsHelper.isEmptyArray(filterValue) ? '' : filterValue.join(', ');
	}

	getSchema() {
		return 'iglu:com.justwatch/filter_context/jsonschema/1-1-0';
	}
}

export class LegacySnowplowPushContext extends SnowplowContext {
	__name = 'legacy_push';
	constructor(
		public pushCampaignId: number,
		public pushId: number,
		public pushRunId: number,
		public dispatchId: string,
		public localizedPlatformMessageId: number,
		public deeplinkTarget: string,
		public deviceLocale: string,
		public appLocale: string,
		public openedTstamp: string | null = null
	) {
		super();
	}

	getSchema() {
		return 'iglu:com.justwatch/push_context/jsonschema/1-0-0';
	}
}

export class SnowplowPushContext extends SnowplowContext {
	__name = 'push';
	constructor(
		public pushType: string,
		public pushTopic: string,
		public pushId: number,
		public pushRunId: number,
		public notificationId: string,
		public userId: string,
		public templateId: number,
		public deeplinkTarget: string,
		public deviceLocale: string,
		public appLocale: string,
		public sentTstamp: string | null
	) {
		super();
	}

	getSchema() {
		return 'iglu:com.justwatch/push_context/jsonschema/2-1-0';
	}
}

export class SnowplowClickoutContext extends SnowplowContext {
	__name = 'clickout';
	// provider offer clickout
	provider: string | null; // clear_name
	providerId: number | undefined;
	monetizationType: string;
	presentationType: string;
	currency: string | undefined;
	price: number | undefined;
	originalPrice: number | undefined;
	// cinema clickout
	audioLanguage: string | undefined;
	subtitleLanguage: string | null;
	showtime: string | undefined;
	partnerId: number | undefined;
	contentType: string | undefined; // sports related, e.g. preview, live, highlight, ...

	static fromProviderOffer(offer: Offer, provider: Provider, sportObjectType?: SportObjectType) {
		const instance = new this();

		instance.provider = provider.clear_name;
		instance.monetizationType = offer.monetization_type || 'unknown';
		instance.presentationType = offer.presentation_type || 'unknown';
		instance.currency = offer.currency;
		instance.price = offer.retail_price;
		instance.originalPrice = offer.last_change_retail_price;
		instance.providerId = provider.id;
		instance.partnerId = 1;
		instance.contentType = sportObjectType;

		return instance;
	}

	getSchema() {
		return 'iglu:com.justwatch/clickout_context/jsonschema/1-3-0';
	}
}

export class SnowplowSportsClickoutContext extends SnowplowContext {
	__name = 'clickout';
	constructor(
		public monetizationType: string,
		public presentationType: string,
		public providerId: number,
		public showtime: string,
		public provider: string,
		public partnerId: number,
		public currency: string
	) {
		super();
	}

	getSchema() {
		return 'iglu:com.justwatch/clickout_context/jsonschema/1-2-0';
	}
}

// Use clickout context 1-2-0 for affiliate links
export class SnowplowClickoutContextAffiliateLink extends SnowplowContext {
	__name = 'clickout';
	// provider offer clickout
	provider: string | null; // clear_name
	providerId: number | undefined;
	monetizationType: string;
	presentationType: string;
	currency: string | undefined;
	price: number | undefined;
	originalPrice: number | undefined;
	// cinema clickout
	audioLanguage: string | undefined;
	subtitleLanguage: string | null;
	showtime: string | undefined;
	partnerId: number | undefined;

	static fromProviderOffer(offer: ClickoutOfferDetails) {
		const instance = new this();

		// @ts-ignore
		const bundleId: number | null = offer.bundleId ?? null;

		instance.provider = offer.package.clearName;
		instance.monetizationType = offer.monetizationType ? toRESTMonetizationType(offer.monetizationType) : 'unknown';
		instance.presentationType = offer.presentationType ? toRESTPresentationType(offer.presentationType) : 'unknown';
		instance.providerId = bundleId ?? offer.package.packageId;
		instance.partnerId = 1;

		if (offer.retailPriceValue) instance.price = offer.retailPriceValue;
		if (offer.currency) instance.currency = offer.currency;
		if (offer.lastChangeRetailPriceValue) instance.originalPrice = offer.lastChangeRetailPriceValue;

		return instance;
	}

	getSchema() {
		return 'iglu:com.justwatch/clickout_context/jsonschema/1-2-0';
	}
}

export class SnowplowClickoutContextGraphql extends SnowplowContext {
	__name = 'clickout';
	// provider offer clickout
	provider: string | null; // clear_name
	providerId: number | undefined;
	monetizationType: string;
	presentationType: string;
	currency: string | undefined;
	price: number | undefined;
	originalPrice: number | undefined;
	// cinema clickout
	audioLanguage: string | undefined;
	subtitleLanguage: string | null;
	showtime: string | undefined;
	partnerId: number | undefined;
	contentType: ClickoutContentType | undefined;

	static fromProviderOffer(
		offer: ClickoutOfferDetails,
		contentType?: SportObjectType | ClickoutContentType.LeavingSoon
	) {
		const instance = new this();

		instance.provider = offer.package.clearName;
		instance.monetizationType = offer.monetizationType ? toRESTMonetizationType(offer.monetizationType) : 'unknown';
		instance.presentationType = offer.presentationType ? toRESTPresentationType(offer.presentationType) : 'unknown';
		instance.providerId = offer.package.packageId;
		instance.partnerId = 1;
		instance.contentType = this.getContentType(contentType);

		if (offer.retailPriceValue) instance.price = offer.retailPriceValue;
		if (offer.currency) instance.currency = offer.currency;
		if (offer.lastChangeRetailPriceValue) instance.originalPrice = offer.lastChangeRetailPriceValue;

		return instance;
	}

	static getContentType(
		contentType?: SportObjectType | ClickoutContentType.LeavingSoon
	): ClickoutContentType | undefined {
		switch (contentType) {
			case SportObjectType.MainEvent:
				return ClickoutContentType.MainEvent;
			case SportObjectType.Highlight:
				return ClickoutContentType.Highlight;
			case SportObjectType.Interview:
				return ClickoutContentType.Interview;
			case ClickoutContentType.LeavingSoon:
				return ClickoutContentType.LeavingSoon;
			default:
				return undefined;
		}
	}

	getSchema() {
		return 'iglu:com.justwatch/clickout_context/jsonschema/1-3-0';
	}
}

export class SnowplowQuestionContext extends SnowplowContext {
	__name = 'question';
	constructor(
		public surveyId: number,
		public surveySession: string,
		public sequenceNumber: number,
		public nodeId: number,
		public elementId: number,
		public subtype: string,
		public translationId: number,
		public variationId: number,
		public locale: string, // 5 char locale e.g. de_DE
		public tagId?: number
	) {
		super();
	}

	getSchema() {
		return 'iglu:com.justwatch/question_context/jsonschema/1-0-0';
	}
}

export class SnowplowAnswerContext extends SnowplowContext {
	__name = 'answer';
	constructor(
		public isSelected: boolean,
		public answerIndex: number,
		public nodeId: number,
		public elementId: number,
		public subtype: 'simple' | 'free_text',
		public translationId: number,
		public variationId: number,
		public textualAnswer?: string
	) {
		super();
	}

	getSchema() {
		return 'iglu:com.justwatch/answer_context/jsonschema/1-0-0';
	}
}

export class SnowplowIntentContext extends SnowplowContext {
	__name = 'intent';
	constructor(public id: number, public technicalName: string) {
		super();
	}

	getSchema() {
		return 'iglu:com.justwatch/intent_context/jsonschema/1-0-0';
	}
}

export class SnowplowModuleContext extends SnowplowContext {
	__name = 'module';
	constructor(
		public moduleId: string,
		public moduleIdx: number,
		public templateTechnicalName: string,
		public discoverySessionId: string,
		public anchorValue?: string
	) {
		super();
	}

	getSchema() {
		return 'iglu:com.justwatch/module_context/jsonschema/1-0-0';
	}
}

export class SnowplowImpressionContext extends SnowplowContext {
	__name = 'impression';
	titles: string;

	constructor(public length: number, public indexOffset: number, entityTitles: string[], public seenUntil?: number) {
		super();

		this.titles = JSON.stringify(entityTitles);
	}

	getSchema() {
		return 'iglu:com.justwatch/impression_context/jsonschema/1-0-0';
	}
}

export class SnowplowImpressionContext2 extends SnowplowContext {
	__name = 'impression_v2';
	constructor(public titleCodes: string[], public arrayLength: number) {
		super();
	}

	getSchema() {
		return 'iglu:com.justwatch/impression_context/jsonschema/2-0-0';
	}
}

export class SnowplowMobileContext extends SnowplowContext {
	__name = 'mobile';
	constructor(
		public osType: string,
		public osVersion: string,
		public deviceManufacturer: string,
		public deviceModel: string,
		public carrier: string | null,
		public openIdfa: string | null,
		public appleIdfa: string | null,
		public appleIdfv: string | null,
		public androidIdfa: string | null
	) {
		super();
	}

	getSchema() {
		return 'iglu:com.snowplowanalytics.snowplow/mobile_context/jsonschema/1-0-0';
	}
}

export class SnowplowPageTypeContext extends SnowplowContext {
	__name = 'page_type';
	constructor(public pageType: string, public appLocale: string, public appLanguage: string) {
		super();
	}

	getSchema() {
		return 'iglu:com.justwatch/page_type_context/jsonschema/1-0-0';
	}
}

// CHANGE identifier VALUE IF NEEDED
export class SnowplowDevelopmentTestContext extends SnowplowContext {
	__name = 'development_test';
	constructor(public testEvent: boolean, public identifier: string = 'staging-environment') {
		super();
	}

	getSchema() {
		return 'iglu:com.justwatch/development_test_context/jsonschema/1-0-0';
	}
}

/**
 * Snowplow experiment context
 */
export class SnowplowExperimentContext extends SnowplowContext {
	__name = 'experiment';
	constructor(
		public experimentName: string,
		public version: string,
		public variant: string,
		public description: string = ''
	) {
		super();
	}

	getSchema() {
		return 'iglu:com.justwatch/experiment_context/jsonschema/1-0-0';
	}
}

/**
 * Snowplow performance context
 */
export class SnowplowPerformanceContext extends SnowplowContext {
	__name = 'performance';
	network_information: string;
	general_timings: string;
	navigation_timings: string;
	data_consumption: string;

	constructor(performanceMetrics: PerformanceMetrics, navigation?: PerformanceNavigationTiming) {
		super();
		this.general_timings = JSON.stringify(performanceMetrics.generalTimings);
		this.network_information = JSON.stringify(performanceMetrics.networkInformation);
		this.navigation_timings = JSON.stringify(navigation || {});
		this.data_consumption = JSON.stringify(performanceMetrics.dataConsumption);
	}

	getSchema() {
		return 'iglu:com.justwatch/performance_metrics_context/jsonschema/1-0-0';
	}
}

export class SnowplowSportContext extends SnowplowContext {
	__name = 'sport';
	sportTechName: string;
	// https://gitlab.justwatch.com/jw/jw-dwh/-/blob/master/snowplow-schemas/iglu-schemas/schemas/com.justwatch/sport_context/jsonschema/1-0-0#L17
	objectType: 'sport' | 'competition' | 'season' | 'event' | 'venue' | 'competitor' | 'generic' | undefined;
	competitionId: number | undefined;
	seasonId: number | undefined;
	eventId: number | undefined;
	event:
		| {
				status: string;
				matchStatus?: string;
				startAt?: string;
				endAt?: string | null;
				winnerId?: string | undefined; // nice to have
		  }
		| undefined;
	venueId: number | undefined; // nice to have
	competitorIdHome: number | undefined; // nice to have
	competitorIdGuest: number | undefined; // nice to have

	// competitors: Competitor[]; // future
	constructor(sportTechName: SnowplowSportContext['sportTechName'], rawObjectType: ObjectType | SportPageType) {
		super();
		let objectType: SnowplowSportContext['objectType'] = undefined;
		switch (rawObjectType) {
			case ObjectType.SportCompetitor:
				objectType = 'competitor';
				break;
			case SportPageType.SportCompetition:
			case ObjectType.SportCompetition:
				objectType = 'competition';
				break;
			case SportPageType.SportEvent:
			case ObjectType.SportEvent:
				objectType = 'event';
				break;
			case SportPageType.SportOverview:
			case ObjectType.Sport:
				objectType = 'sport';
				break;
			case SportPageType.AllSports:
				objectType = 'generic';
				break;
		}
		this.objectType = objectType;
		this.sportTechName = sportTechName;
	}

	getSchema() {
		return 'iglu:com.justwatch/sport_context/jsonschema/1-0-0';
	}
	static getEventContent(event: SingleStepSportEvent | MultiStepSportEvent) {
		return 'parent' in event
			? (event as MultiStepSportEvent).parent!.content
			: (event as SingleStepSportEvent).content;
	}
	static fromSportEventV2(
		sportTechName: SportType,
		objectType: ObjectType | SportPageType,
		event: SingleStepSportEvent | MultiStepSportEvent
	) {
		const sportContext: SnowplowSportContext | null = new SnowplowSportContext(sportTechName, objectType);
		const eventContent = SnowplowSportContext.getEventContent(event);
		sportContext.competitionId = event.competition?.objectId;
		sportContext.seasonId = event.competition?.activeSeason?.seasonID;
		sportContext.event = {
			status: eventContent.status,
			startAt: eventContent?.startAt,
			endAt: eventContent?.endAt,
		};
		sportContext.eventId = event.objectId;
		sportContext.venueId = eventContent?.venue?.venueID || undefined; // to prevent sending venueID = 0

		// check for competitors (FootballEvent)
		if ('competitors' in event) {
			sportContext.competitorIdHome = event.competitors?.homeTeam?.competitors.map(c => c.objectId).find(Boolean);
			sportContext.competitorIdGuest = event.competitors?.awayTeam?.competitors
				.map(c => c.objectId)
				.find(Boolean);
		}
		// chck for content.matchStatus (FootballEvent)
		if ('matchStatus' in event.content && 'competitors' in event) {
			const winnerId = [event.competitors?.homeTeam, event.competitors?.awayTeam]
				.filter(team => team.winner)
				.map(team => team.competitors.map(c => c.id).find(Boolean))
				.find(Boolean);
			sportContext.event.winnerId = winnerId;
			// sportContext.event.matchStatus = event.content.matchStatus;
		}

		return sportContext;
	}
}

export class SnowplowCoreWebVitalsContext extends SnowplowContext {
	__name = 'corewebvitals';
	metrics: readonly {
		name: 'CLS' | 'FCP' | 'FID' | 'INP' | 'LCP' | 'TTFB';
		id: string;
		delta: number;
		value: number;
	}[];

	constructor(data: Set<Metric>) {
		super();

		this.metrics = Object.freeze(
			Array.from(data).map(({ id, name, value, delta }) => ({
				id,
				name,
				value: Math.round(value * 100) / 100,
				delta: Math.round(name === 'CLS' ? delta * 1000 : delta),
			}))
		);
	}

	getSchema() {
		return 'iglu:com.justwatch/core_web_vitals_context/jsonschema/1-0-0';
	}
}

export const CONTEXT_CLASSES = {
	title: SnowplowTitleContext,
	person: SnowplowPersonContext,
	campaign_v1: SnowplowCampaignContextV1,
	campaign_v2: SnowplowCampaignContextV2,
	geo: SnowplowGeoContext,
	login: SnowplowLoginContext,
	filter: SnowplowFilterContext,
	legacy_push: LegacySnowplowPushContext,
	push: SnowplowPushContext,
	clickout: SnowplowClickoutContext,
	sports_clickout: SnowplowSportsClickoutContext, // unused
	question: SnowplowQuestionContext,
	answer: SnowplowAnswerContext,
	intent: SnowplowIntentContext,
	module: SnowplowModuleContext,
	impression: SnowplowImpressionContext,
	impression_v2: SnowplowImpressionContext2,
	mobile: SnowplowMobileContext,
	page_type: SnowplowPageTypeContext,
	development_test: SnowplowDevelopmentTestContext,
	experiment: SnowplowExperimentContext,
	performance: SnowplowPerformanceContext,
	freemium: SnowplowFreemiumContext,
	packages: SnowplowPackageContext,
	sport: SnowplowSportContext,
	core_web_vitals: SnowplowCoreWebVitalsContext,
};
